import React, { useState, useEffect, Component, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useChatContext } from '../../contexts/ChatContext';
import toast from 'react-hot-toast';
import { logger } from '../../utils/logger';
import { isAdminRole } from '../../utils/authRoles';

const ChatLayout = lazy(() =>
  import('./ChatLayout').then((m) => ({ default: m.ChatLayout }))
);

class ChatErrorBoundary extends Component<
  { children: React.ReactNode; onRetry?: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      logger.error('[GlobalChatOverlay] Chat overlay error', error, info);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <p className="text-primary-600 mb-2">Chat failed to load</p>
          <p className="text-sm text-primary-500/70 mb-6">
            Please try again. If the problem persists, contact support.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const GlobalChatOverlay: React.FC = () => {
  const { isChatOpen, closeChat } = useChatContext();
  const { user } = useAuth();
  
  const [chatRetryKey, setChatRetryKey] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(() => {
    try {
      const raw = localStorage.getItem('voluntas.chat.admin.selectedClient');
      return raw || undefined;
    } catch { return undefined; }
  });

  useEffect(() => {
    if (user?.role === 'client') {
      const effective = (user as any).clientId || (user as any).primary_application_id;
      if (effective) setSelectedClientId(effective);
    }
  }, [user]);

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    try { localStorage.setItem('voluntas.chat.admin.selectedClient', clientId); } catch {}
    
    if (isAdminRole(user?.role)) {
      toast.success('Switched to client chat context', { duration: 2000 });
    }
  };

  const effectiveClientId = user?.role === 'client' 
    ? ((user as any).clientId || (user as any).primary_application_id)
    : selectedClientId;

  // Do not render anything if user is not authenticated or not approved (for clients)
  if (!user) return null;
  if (user.role === 'target_viewer') return null;
  if (user.role === 'client' && (!user.approved || user.approvalStatus !== 'approved')) return null;

  return (
    <AnimatePresence>
      {isChatOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-white z-[101] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-primary-500/10 bg-white">
            <h2 className="text-lg font-semibold text-primary-500">AI Assistant</h2>
            <button
              onClick={closeChat}
              className="p-2 rounded-full text-primary-500/60 hover:text-primary-500 hover:bg-primary-500/5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              aria-label="Close chat"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Chat Content */}
          <div className="flex-1 min-h-0 overflow-hidden relative">
            <ChatErrorBoundary
              key={chatRetryKey}
              onRetry={() => setChatRetryKey((k) => k + 1)}
            >
              <Suspense
                fallback={
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-primary-500/70">
                    <div className="w-8 h-8 border-2 border-primary-500/20 border-t-accent-500 rounded-full animate-spin" />
                    <p className="text-sm">Loading chat…</p>
                  </div>
                }
              >
                <ChatLayout
                  selectedClientId={effectiveClientId}
                  onClientChange={isAdminRole(user.role) ? handleClientChange : undefined}
                />
              </Suspense>
            </ChatErrorBoundary>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
